import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

export const FCM_HOMEPAGE_PETITION_MARQUEE = defineFcm('homepage_petition_marquee')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				isEnabled: { type: 'boolean' },
				petitions: {
					type: 'array',
					shape: {
						type: 'object',
						shape: {
							petitionId: { type: 'number' },
							summarizedTitle: { type: 'string' },
							// optional so entries curated before this flag existed keep normalizing
							usePetitionPhoto: { type: 'boolean', optional: true },
							// optional - an entry without one defaults to the PETITION card
							cardType: {
								type: 'enum',
								values: [
									'PETITION',
									'SUPPORTER_VOICE',
									'STARTER_UPDATE',
									'DECISION_MAKER',
									'ENDORSEMENT',
									'MOMENTUM',
									'CLOSE_TO_GOAL',
									'MILESTONE',
								] as const,
								optional: true,
							},
							// interpreted by cardType - see the description below
							pinnedId: { type: 'string', optional: true },
						},
					},
				},
			},
		}),
	)
	.withDefault({
		isEnabled: false,
		petitions: [],
	})
	.toConfig({
		owner: 'squad-narwhal',
		scope: 'corgi-middle-end',
		supportedGroups: { countries: true },
		context: 'Homepage petition marquee',
		description:
			'Curated petitions shown in the homepage marquee. The petition ID is used to grab associated data. \n The `summarizedTitle` is used as a petition title for the rendered card. `usePetitionPhoto` determines whether a user’s avatar or petition photo is used for the card. An entry with no cardType defaults to PETITION.\ncardType picks which card shape the entry renders and therefore what is pulled from the petition.\n\nPETITION is the signature-count card - it renders as a victory when the petition has won, and as trending otherwise.\nSUPPORTER_VOICE pulls a supporter comment (videos excluded).\nSTARTER_UPDATE, DECISION_MAKER and ENDORSEMENT each pull the matching kind of petition update. MILESTONE pulls the update marking a signature count.\nMOMENTUM uses the petition’s own daily and weekly signature counts and needs no extra lookup.\nCLOSE_TO_GOAL shows the petition’s progress toward its signature goal and needs no extra lookup (an entry whose petition has no signature goal is skipped).\n\n`pinnedId` pins one exact item rather than letting the marquee auto-pick, and is read according to cardType - a comment ID for SUPPORTER_VOICE, a petition update ID for STARTER_UPDATE, DECISION_MAKER and MILESTONE, ignored for PETITION, ENDORSEMENT (which always shows the latest endorsement), MOMENTUM and CLOSE_TO_GOAL. A pin that does not match the cardType (for example a MILESTONE entry pinning a starter update, or a SUPPORTER_VOICE entry pinning a video comment) is treated as unshowable. An entry whose pinned or auto-picked item cannot be shown (for example a petition with supporter comments hidden) is skipped, and the remaining petitions fill the row.',
		template: {
			isEnabled: true,
			petitions: [
				{
					petitionId: 123456,
					summarizedTitle: 'Protect the national parks',
					usePetitionPhoto: false,
					cardType: 'PETITION',
				},
				{
					petitionId: 789012,
					summarizedTitle: 'Save the local library',
					usePetitionPhoto: false,
					cardType: 'SUPPORTER_VOICE',
				},
				{
					petitionId: 345678,
					summarizedTitle: 'Fix the crossing on Main Street',
					usePetitionPhoto: false,
					// pinnedId is a petition update ID here because cardType is update-backed
					cardType: 'STARTER_UPDATE',
					pinnedId: '4567890',
				},
			],
		},
	});
